import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  MISSION_ERROR,
  confirmMissionDraft,
  discardMissionDraft,
  getMissionDraft,
  missionErrorCode,
} from "../../../Services/journey";
import { trackEvent } from "../../../Services/amplitude";
import { showSuccessToast } from "../../../Utils/toast";
import { JourneyBackHeader, JourneySubpageCard, useStandaloneHeader } from "../shared";
import { MISSION_EVENT, missionPartLabel, missionRoutes } from "./evolution";
import { forgetEvolutionChat } from "./evolutionChat";
import { DimensionCards, MissionStatementHero } from "./MissionParts";
import { REVIEW_COPY as C } from "./copy";
import "../style.css";
import "./style.css";

/**
 * Review an evolved Mission — `/journey/mission/review/:draftId`.
 *
 * `GET /journey/mission/drafts/{id}`: the proposed Mission with an "Evolved"
 * chip on each changed part. Review Changes is a read-only comparison of
 * `current` vs `proposed` for each changed part plus `change_summary` — not an
 * editor. Only Save (`…/confirm`, idempotent) changes the Mission; Keep
 * Current (`…/discard`) and leaving both leave it as it was. No Figma frame
 * exists for this screen; it is composed from the Mission screen's parts.
 */

const TERMINAL = new Set([
  MISSION_ERROR.DRAFT_DISCARDED,
  MISSION_ERROR.UNKNOWN_DRAFT,
  MISSION_ERROR.EVOLUTION_SESSION_CLOSED,
]);

function MissionReview() {
  const { draftId } = useParams();
  const navigate = useNavigate();
  useStandaloneHeader();

  const [state, setState] = useState({ loading: true, error: false, draft: null });
  // A decision that ends the review in place: "decided" | "rebased" | null.
  const [closed, setClosed] = useState(null);
  const [showChanges, setShowChanges] = useState(false);
  const [busy, setBusy] = useState(null); // "save" | "keep" | null
  const [actionError, setActionError] = useState(null);
  const busyRef = useRef(false);

  const load = useCallback(async () => {
    setState({ loading: true, error: false, draft: null });
    try {
      const draft = await getMissionDraft(draftId);
      setState({ loading: false, error: false, draft });
      if (draft?.status && draft.status !== "DRAFT") setClosed("decided");
    } catch (e) {
      console.error("Failed to load the evolved Mission", e);
      if (TERMINAL.has(missionErrorCode(e))) {
        setState({ loading: false, error: false, draft: null });
        setClosed("decided");
      } else {
        setState({ loading: false, error: true, draft: null });
      }
    }
  }, [draftId]);

  useEffect(() => {
    load();
  }, [load]);

  const { draft } = state;
  const changed = Array.isArray(draft?.changed) ? draft.changed : [];
  const changedSet = new Set(changed);

  const toggleChanges = () => {
    if (!showChanges) trackEvent(MISSION_EVENT.REVIEW_CHANGES_OPENED);
    setShowChanges((v) => !v);
  };

  const finish = () => {
    forgetEvolutionChat(draft?.evolution_session_id);
    navigate(missionRoutes.mission, { replace: true });
  };

  const run = async (kind) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(kind);
    setActionError(null);
    try {
      if (kind === "save") {
        const res = await confirmMissionDraft(draftId);
        if (!res?.already_confirmed) trackEvent(MISSION_EVENT.SAVED);
        showSuccessToast(C.saved);
      } else {
        await discardMissionDraft(draftId);
        trackEvent(MISSION_EVENT.KEPT_CURRENT);
        showSuccessToast(C.kept);
      }
      finish();
    } catch (e) {
      const code = missionErrorCode(e);
      if (code === MISSION_ERROR.MISSION_REBASE_REQUIRED) setClosed("rebased");
      else if (TERMINAL.has(code)) setClosed("decided");
      else setActionError(kind === "save" ? C.saveError : C.discardError);
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  let body;
  if (state.loading) {
    body = <div className="journey-card journey-card--status">{C.loading}</div>;
  } else if (closed) {
    body = (
      <div className="journey-card journey-card--status" role="status">
        <p style={{ margin: "0 0 16px" }}>{closed === "rebased" ? C.alreadyEvolved : C.alreadyDecided}</p>
        <button type="button" className="journey-solid-btn" onClick={finish}>
          {C.backToMission}
        </button>
      </div>
    );
  } else if (state.error || !draft) {
    body = (
      <div className="journey-card journey-card--status" role="alert">
        <p style={{ margin: "0 0 12px" }}>{C.loadError}</p>
        <button type="button" className="journey-text-btn" onClick={load}>
          {C.retry}
        </button>
      </div>
    );
  } else {
    body = (
      <>
        <p className="journey-mission-review__lead">{C.lead}</p>
        <MissionStatementHero
          statement={draft.proposed?.statement}
          chip={changedSet.has("statement") ? C.evolvedChip : null}
        />
        <DimensionCards
          dimensions={draft.proposed?.dimensions}
          evolved={changedSet}
          evolvedLabel={C.evolvedChip}
        />

        {showChanges && (
          <section className="journey-card journey-mission-review__changes" aria-label={C.changesTitle}>
            <h2 className="journey-card__title">{C.changesTitle}</h2>
            {draft.change_summary && (
              <p className="journey-mission-review__summary">{draft.change_summary}</p>
            )}
            {changed.map((key) => {
              const before =
                key === "statement" ? draft.current?.statement : draft.current?.dimensions?.[key];
              const now =
                key === "statement" ? draft.proposed?.statement : draft.proposed?.dimensions?.[key];
              return (
                <div className="journey-mission-review__change" key={key}>
                  <p className="journey-card__eyebrow" style={{ color: "var(--journey-accent)" }}>
                    {missionPartLabel(key).toUpperCase()}
                  </p>
                  <div className="journey-mission-review__compare">
                    <div>
                      <span className="journey-mission-review__tag">{C.before}</span>
                      <p className="journey-mission-review__text">{before || "—"}</p>
                    </div>
                    <div>
                      <span className="journey-mission-review__tag journey-mission-review__tag--now">
                        {C.now}
                      </span>
                      <p className="journey-mission-review__text">{now || "—"}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </section>
        )}

        {actionError && (
          <p className="journey-evolve__error" role="alert">
            {actionError}
          </p>
        )}

        <div className="journey-mission-review__actions">
          <button
            type="button"
            className="journey-solid-btn"
            disabled={Boolean(busy)}
            onClick={() => run("save")}
          >
            {busy === "save" ? C.saving : C.save}
          </button>
          {changed.length > 0 && (
            <button
              type="button"
              className="journey-outline-btn"
              aria-expanded={showChanges}
              onClick={toggleChanges}
            >
              {showChanges ? C.hideChanges : C.reviewChanges}
            </button>
          )}
          <button
            type="button"
            className="journey-text-btn journey-mission-review__keep"
            disabled={Boolean(busy)}
            onClick={() => run("keep")}
          >
            {C.keepCurrent}
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="journey-page">
      <div className="journey-page__inner">
        <JourneyBackHeader to={missionRoutes.mission} title={C.title} />
        <JourneySubpageCard>{body}</JourneySubpageCard>
      </div>
    </div>
  );
}

export default MissionReview;
